import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useUserId } from "@/hooks/useUserId";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useSubscription } from "@/hooks/useSubscription";
import { processWebhooks } from "@/utils/webhook";
import { InspirationSource } from "@/types/webhook";
import { Target, ArrowLeft, Check, Sparkles, LogOut, Lock } from "lucide-react";
import { extractInstagramUsername, validateInstagramInput, isValidInstagramUsername, isRealInstagramUsername } from "@/utils/instagramUsername";
import OnboardingPricingStep from "@/components/onboarding/OnboardingPricingStep";
import { useProfileVerification, VerifiableProfileInput, ProfileAvatar } from "@/components/onboarding/ProfileVerification";
import VideoTutorialHint from "@/components/onboarding/VideoTutorialHint";
import { logEdgeFunctionError, logError } from "@/utils/sentry";
import { parseStoredCategory } from "@/constants/categoryTaxonomy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MAX_COMPETITORS = 1; // Trial — 1 active competitor. Pro allows up to 6.
const LOCKED_SLOTS = 2; // Visual-only locked slots shown beneath the active input.
const CONFIRMED_KEY = "onboarding_competitors_confirmed";

// Stable canonical signature of the verified username set so we can decide
// whether the user has already approved this exact lineup of competitors.
const buildConfirmedSignature = (usernames: string[]) =>
  JSON.stringify([...usernames].sort());

const STEPS = [
  { id: 1, label: 'Profil' },
  { id: 2, label: 'Branża' },
  { id: 3, label: 'Inspiracje' },
  { id: 4, label: 'Konkurencja' },
  { id: 5, label: 'Płatność' },
  { id: 6, label: 'Start' },
];

type PageState = "form" | "pricing" | "processing" | "success";

const AddCompetitors = () => {
  const [competitors, setCompetitors] = useState<string[]>([""]);
  const [validationErrors, setValidationErrors] = useState<{ [key: number]: string }>({});
  const [pageState, setPageState] = useState<PageState>("form");
  const [preselectedPlan, setPreselectedPlan] = useState<{ planName: string; billing: "monthly" | "yearly" } | null>(null);
  const [webhookProgress, setWebhookProgress] = useState({
    completed: 0,
    total: 0,
    currentSource: null as InspirationSource | null,
    isProcessing: false,
  });
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { signOut } = useAuth();
  const userId = useUserId();
  const { saveInspirationSources } = useOnboarding();
  const { refresh: refreshSubscription } = useSubscription();
  // After a successful verification, replace the raw input with the
  // canonical "@handle" and invalidate any prior confirmation — a fresh
  // verification means the lineup may have changed since the user last
  // approved it.
  const handleVerifySuccess = (index: number, verifiedUsername: string) => {
    setCompetitors(prev => {
      const next = [...prev];
      const canonical = `@${verifiedUsername}`;
      if (next[index] !== canonical) next[index] = canonical;
      return next;
    });
    localStorage.removeItem(CONFIRMED_KEY);
  };
  const { verifications, verifyProfile, clearVerification, removeAndReindex, hasUnverifiedInputs } = useProfileVerification(
    userId,
    "onboarding_competitor_verifications",
    handleVerifySuccess,
  );

  // Detect return from Stripe checkout. Guard against re-running on reload.
  const postPaymentRanRef = useRef(false);
  useEffect(() => {
    if (
      searchParams.get("checkout") === "success" &&
      userId &&
      !postPaymentRanRef.current
    ) {
      postPaymentRanRef.current = true;
      setPageState("processing");
      runPostPaymentProcessing();
    }
  }, [searchParams, userId]);

  // Animate progress during processing
  useEffect(() => {
    if (pageState === "processing" && webhookProgress.total === 0) {
      setAnimatedProgress(0);
      const TOTAL_DURATION_MS = 60000;
      const MAX_PROGRESS = 95;
      const INTERVAL_MS = 200;
      const increment = (MAX_PROGRESS / TOTAL_DURATION_MS) * INTERVAL_MS;
      const interval = setInterval(() => {
        setAnimatedProgress((prev) => {
          const next = prev + increment;
          return next >= MAX_PROGRESS ? MAX_PROGRESS : next;
        });
      }, INTERVAL_MS);
      return () => clearInterval(interval);
    } else if (pageState !== "processing") {
      setAnimatedProgress(0);
    }
  }, [pageState, webhookProgress.total]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Restore saved competitors
  useEffect(() => {
    const saved = localStorage.getItem("onboarding_add_competitors");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) setCompetitors(parsed);
      } catch {
        // ignore
      }
    }
  }, []);

  // Persist competitors
  useEffect(() => {
    if (competitors.length > 0) {
      localStorage.setItem("onboarding_add_competitors", JSON.stringify(competitors));
    }
  }, [competitors]);

  const addCompetitor = () => {
    if (competitors.length < MAX_COMPETITORS) setCompetitors([...competitors, ""]);
  };

  const removeCompetitor = (index: number) => {
    if (competitors.length > 1) {
      setCompetitors(competitors.filter((_, i) => i !== index));
      removeAndReindex(index);
      // Lineup changed — invalidate the prior confirmation.
      localStorage.removeItem(CONFIRMED_KEY);
    }
  };

  const updateCompetitor = (index: number, value: string) => {
    const updated = [...competitors];
    updated[index] = value.toLowerCase();
    setCompetitors(updated);

    clearVerification(index);
    // Input edited — any prior confirmation no longer applies.
    localStorage.removeItem(CONFIRMED_KEY);

    const errors = { ...validationErrors };
    if (value.trim() !== "") {
      const error = validateInstagramInput(value);
      if (error) errors[index] = error;
      else delete errors[index];
    } else {
      delete errors[index];
    }
    setValidationErrors(errors);
  };

  const [isCheckingProfile, setIsCheckingProfile] = useState(false);

  // Session-scoped cache: once the pre-payment Instagram guard has passed
  // for this user in this tab, skip the DB roundtrip on subsequent clicks.
  // Cleared on payment completion and on userId mismatch so a different
  // logged-in user always re-checks. The guard's safety property is
  // preserved because instagram_username can only ever be added (never
  // changed) outside of onboarding — once it's real, it stays real.
  const PAYMENT_GUARD_PASSED_KEY = "onboarding_payment_guard_passed";

  // Step 5 (pricing) is the next state after the user confirms. Centralise
  // the transition so both the cache-hit path and the "Tak, kontynuuj"
  // dialog handler use identical logic.
  const proceedToPricing = () => {
    saveCompetitorsToStorage();
    const savedPlan = localStorage.getItem("selected_plan");
    if (savedPlan) {
      try {
        const parsed = JSON.parse(savedPlan);
        localStorage.removeItem("selected_plan");
        setPreselectedPlan(parsed);
      } catch {
        setPreselectedPlan(null);
      }
    }
    setPageState("pricing");
  };

  // Open the confirm dialog OR — if the user has already approved this
  // exact lineup and the saved signature still matches — go straight to
  // pricing without re-asking. Step 4 is optional, so an empty form is
  // also a valid "no competitors" choice and shouldn't surface a dialog
  // with nothing to confirm.
  const requestConfirmThenProceed = () => {
    const verifiedUsernames = Object.values(verifications)
      .filter(v => v.status === "success" && v.username)
      .map(v => v.username as string);
    if (verifiedUsernames.length === 0) {
      proceedToPricing();
      return;
    }
    const currentSignature = buildConfirmedSignature(verifiedUsernames);
    const savedSignature = localStorage.getItem(CONFIRMED_KEY);
    if (savedSignature === currentSignature) {
      proceedToPricing();
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmContinue = () => {
    const verifiedUsernames = Object.values(verifications)
      .filter(v => v.status === "success" && v.username)
      .map(v => v.username as string);
    localStorage.setItem(
      CONFIRMED_KEY,
      buildConfirmedSignature(verifiedUsernames),
    );
    setShowConfirmDialog(false);
    proceedToPricing();
  };

  const handleStartPayment = async () => {
    if (!userId) return;

    const cachedFor = sessionStorage.getItem(PAYMENT_GUARD_PASSED_KEY);
    if (cachedFor === userId) {
      requestConfirmThenProceed();
      return;
    }

    setIsCheckingProfile(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("instagram_username")
        .eq("user_id", userId)
        .maybeSingle();

      if (!isRealInstagramUsername(profile?.instagram_username)) {
        toast({
          title: "Brakuje nazwy Instagram",
          description:
            "Dodaj swoją nazwę użytkownika Instagram, zanim przejdziesz do płatności.",
          variant: "destructive",
        });
        navigate("/onboarding/instagram");
        return;
      }

      sessionStorage.setItem(PAYMENT_GUARD_PASSED_KEY, userId);
    } catch (error) {
      logError("Onboarding: pre-payment profile check", error, {
        step: "pre-payment-guard",
      });
      toast({
        title: "Coś poszło nie tak",
        description: "Spróbuj jeszcze raz za chwilę.",
        variant: "destructive",
      });
      return;
    } finally {
      setIsCheckingProfile(false);
    }

    requestConfirmThenProceed();
  };

  const saveCompetitorsToStorage = () => {
    localStorage.setItem("onboarding_add_competitors", JSON.stringify(competitors));
  };

  const runPostPaymentProcessing = async () => {
    if (!userId) return;

    try {
      // 1. Reconcile user profile.
      //
      // Earlier onboarding steps already persist their own slice:
      //   • InstagramSetup writes instagram_username + onboarding_started
      //   • IndustrySelection writes category via useOnboarding.saveUserProfile
      //
      // So the post-payment job should NEVER overwrite a real value with "".
      // We read the current row first and only fill in fields that are
      // genuinely missing, falling back to localStorage if needed.
      const { supabase } = await import("@/integrations/supabase/client");

      const { data: existingProfile } = await supabase
        .from("user_profiles")
        .select("instagram_username, category, onboarding_started")
        .eq("user_id", userId)
        .maybeSingle();

      const lsInstagram = localStorage.getItem("onboarding_instagram") || "";
      const storedSubcategories = localStorage.getItem("onboarding_subcategories");
      const { subcategoryIds, legacy: storedIsLegacy } = parseStoredCategory(storedSubcategories);
      const lsCategory =
        storedSubcategories && !storedIsLegacy && subcategoryIds.length
          ? storedSubcategories
          : "";

      const dbInstagram = existingProfile?.instagram_username || "";
      const dbCategory = existingProfile?.category || "";
      const dbHasRealInstagram = isRealInstagramUsername(dbInstagram);

      // Bail out if we have nothing real to write. This avoids creating a
      // placeholder row with the legacy "pending_setup" sentinel just to land
      // a category — the post-payment handler in Subscribe.tsx already
      // reconciled the row when the user actually went through onboarding,
      // and handleConfirmContinue is the canonical writer of
      // `instagram_username`.
      if (!dbHasRealInstagram && !lsInstagram) {
        // Nothing meaningful to upsert; skip to step 2.
      } else {
        const profilePatch: {
          user_id: string;
          updated_at: string;
          onboarding_started: boolean;
          instagram_username: string;
          category?: string;
        } = {
          user_id: userId,
          updated_at: new Date().toISOString(),
          onboarding_started: true,
          instagram_username: dbHasRealInstagram ? dbInstagram : lsInstagram,
        };

        // Only write category when we have a real value. Never blank it.
        if (!dbCategory && lsCategory) {
          profilePatch.category = lsCategory;
        }

        const { error: profileError } = await supabase
          .from("user_profiles")
          .upsert(profilePatch, { onConflict: "user_id" });

        if (profileError) {
          logError("Onboarding: post-payment profile reconcile", profileError, {
            step: "post-payment-profile",
          });
          toast({ title: "Błąd zapisu profilu", variant: "destructive" });
          return;
        }
      }

      // Trigger profile scraping if we now have a real Instagram username on file.
      const effectiveInstagram = dbHasRealInstagram ? dbInstagram : lsInstagram;
      if (isRealInstagramUsername(effectiveInstagram)) {
        try {
          await supabase.functions.invoke("trigger-profile-scraping", {
            body: { username: effectiveInstagram },
          });
        } catch (error) {
          logEdgeFunctionError("trigger-profile-scraping", error, {
            username: effectiveInstagram,
          });
        }
      }

      // 2. Process inspiration sources
      const savedInspirationSources = localStorage.getItem("onboarding_inspiration_sources");
      const inspirationSources: InspirationSource[] = [];
      if (savedInspirationSources) {
        try {
          const parsed = JSON.parse(savedInspirationSources);
          parsed
            .filter((c: string) => c.trim() !== "")
            .forEach((input: string) => {
              const username = extractInstagramUsername(input);
              if (username && isValidInstagramUsername(username)) {
                inspirationSources.push({ username, platform: "instagram" as const });
              }
            });
        } catch {
          // ignore
        }
      }

      if (inspirationSources.length > 0) {
        const saveResult = await saveInspirationSources(userId, inspirationSources);
        if (!saveResult.success) {
          toast({ title: "Błąd zapisu źródeł", variant: "destructive" });
          return;
        }
        await processWebhooks(userId, inspirationSources, (completed, total, currentSource, isProcessing = false) => {
          setWebhookProgress({ completed, total, currentSource, isProcessing });
        });
      }

      // 3. Save competitors (sequential to handle tier-based DB triggers)
      const savedCompetitors = localStorage.getItem("onboarding_add_competitors");
      if (savedCompetitors) {
        try {
          const parsed = JSON.parse(savedCompetitors);
          const validCompetitors = parsed.filter((c: string) => c.trim() !== "");
          for (const input of validCompetitors) {
            const username = extractInstagramUsername(input);
            if (!username) continue;
            const { error } = await supabase.from("competitors").insert({
              user_id: userId,
              instagram_username: username,
              is_active: true,
            });
            if (error) {
              // Unique-constraint violations are expected on retry; only stop on
              // non-duplicate errors (e.g. tier limit) so retries stay idempotent.
              if (error.code !== "23505") {
                console.warn("Competitor insert stopped:", error.message);
                break;
              }
            }
          }
        } catch {
          // ignore
        }
      }

      // 4. Clean up localStorage
      localStorage.removeItem("onboarding_instagram");
      localStorage.removeItem("onboarding_industry");
      localStorage.removeItem("onboarding_inspiration_sources");
      localStorage.removeItem("onboarding_add_competitors");
      localStorage.removeItem("onboarding_subcategories");
      localStorage.removeItem("onboarding_inspiration_verifications");
      localStorage.removeItem("onboarding_competitor_verifications");
      localStorage.removeItem("onboarding_inspiration_confirmed");
      localStorage.removeItem("onboarding_competitors_confirmed");
      sessionStorage.removeItem("onboarding_payment_guard_passed");
      localStorage.removeItem("onboarding_main_category"); // legacy key
      localStorage.removeItem("onboarding_subcategory"); // legacy key
      localStorage.removeItem("onboarding_from_instagram");
      localStorage.setItem("onboarding_completed", "true");

      setPageState("success");
    } catch (error) {
      logError("Onboarding: post-payment processing", error, { step: "post-payment" });
      toast({ title: "Wystąpił błąd", variant: "destructive" });
    }
  };

  const hasValidCompetitors = competitors.some((c) => c.trim() !== "");
  const progressPercentage = webhookProgress.total > 0 ? (webhookProgress.completed / webhookProgress.total) * 100 : 0;

  const currentStep = pageState === "success" ? 6
    : pageState === "processing" ? 5
    : pageState === "pricing" ? 5
    : 4;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((s, index) => (
        <div key={s.id} className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              s.id < currentStep
                ? "bg-primary text-primary-foreground"
                : s.id === currentStep
                ? "bg-primary text-primary-foreground"
                : "bg-white/60 text-gray-500"
            }`}
          >
            {s.id < currentStep ? <Check className="w-4 h-4" /> : s.id}
          </div>
          {index < STEPS.length - 1 && (
            <div className={`w-8 h-0.5 ${s.id < currentStep ? "bg-primary" : "bg-gray-300"}`} />
          )}
        </div>
      ))}
    </div>
  );

  // ─── Success state ───
  if (pageState === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
        <div className="relative z-10 max-w-lg mx-auto px-4 py-8 md:py-16">
          {renderStepIndicator()}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="max-w-md w-full mx-auto backdrop-blur-xl bg-white/40 rounded-3xl shadow-2xl border border-white/50 p-10 text-center space-y-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              className="w-14 h-14 mx-auto rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 flex items-center justify-center"
            >
              <Sparkles className="h-7 w-7 text-[#0080FF]" />
            </motion.div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight leading-tight mb-2">Wszystko gotowe</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Twoje konto jest aktywne. Czas tworzyć viralowe treści.
              </p>
            </div>
            <Button
              size="lg"
              className="w-full h-12 text-base font-semibold rounded-xl bg-[#0080FF] hover:bg-[#006AD5] text-white transition-all active:scale-[0.98]"
              onClick={async () => {
                await refreshSubscription();
                navigate("/panel", { replace: true });
              }}
            >
              Przejdź do panelu
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ─── Processing state ───
  if (pageState === "processing") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
        <div className="relative z-10 max-w-lg mx-auto px-4 py-8 md:py-16">
          {renderStepIndicator()}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="max-w-md w-full mx-auto backdrop-blur-xl bg-white/40 rounded-3xl shadow-2xl border border-white/50 p-10"
          >
            <div className="text-center space-y-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="w-14 h-14 mx-auto rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 flex items-center justify-center"
              >
                <Sparkles className="h-7 w-7 text-[#0080FF]" />
              </motion.div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 tracking-tight leading-tight mb-2">
                  {webhookProgress.total === 0
                    ? "Przygotowujemy Twoje konto"
                    : `Przetwarzanie ${webhookProgress.completed + 1} z ${webhookProgress.total}`}
                </h2>
                <p className="text-sm text-gray-500">
                  {webhookProgress.currentSource
                    ? `@${webhookProgress.currentSource.username}`
                    : "Analizujemy Twój profil i zbieramy dane..."}
                </p>
              </div>
              <Progress value={webhookProgress.total === 0 ? animatedProgress : progressPercentage} className="h-1.5 [&>div]:bg-[#0080FF]" />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ─── Pricing state (inline step) ───
  if (pageState === "pricing") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />

        <div className="relative z-20 flex justify-end px-4 pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={signOut}
            className="text-gray-500 hover:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Wyloguj się
          </Button>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto px-4 py-4 md:py-12">
          {renderStepIndicator()}
          <OnboardingPricingStep
            initialPlan={preselectedPlan}
            onBack={() => setPageState("form")}
          />
        </div>
      </div>
    );
  }

  // ─── Form state ───
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent" />
      <div className="absolute top-1/4 -right-48 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 -left-48 w-96 h-96 bg-secondary/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: "1s" }} />

      {/* Logout button - top right */}
      <div className="relative z-20 flex justify-end px-4 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="text-gray-500 hover:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Wyloguj się
        </Button>
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-4 py-4 md:py-12">
        {renderStepIndicator()}

        <div className="flex justify-center mb-6">
          <VideoTutorialHint />
        </div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white/80 rounded-2xl shadow-lg border border-white/60 p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100/50 text-sm text-gray-500 mb-4">
              <span>Opcjonalne</span>
            </div>
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-secondary to-secondary/80 rounded-2xl flex items-center justify-center shadow-lg">
              <Target className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight text-gray-900 mb-2">Obserwuj rynek</h1>
            <p className="text-gray-500 leading-relaxed">Dodaj profile konkurencji do analizy</p>
          </div>

          <div className="space-y-3">
            {competitors.map((competitor, index) => (
              <VerifiableProfileInput
                key={index}
                index={index}
                value={competitor}
                onChange={updateCompetitor}
                onRemove={removeCompetitor}
                onVerify={verifyProfile}
                onEnterAdd={addCompetitor}
                verification={verifications[index]}
                validationError={validationErrors[index]}
                canRemove={competitors.length > 1}
                icon={<Target className="h-4 w-4" />}
                totalCount={competitors.length}
              />
            ))}

            {Array.from({ length: LOCKED_SLOTS }).map((_, i) => (
              <div
                key={`locked-${i}`}
                aria-hidden="true"
                className="flex items-center gap-2 w-full h-11 px-3 rounded-xl border border-dashed border-gray-300 bg-gray-100/60 text-gray-400 select-none"
              >
                <Lock className="h-4 w-4" />
                <span className="text-sm">Po aktywacji</span>
              </div>
            ))}
          </div>

          <div className="space-y-3 mt-6">
            <Button
              onClick={handleStartPayment}
              className="w-full h-12 text-base font-medium"
              size="lg"
              disabled={
                Object.keys(validationErrors).length > 0 ||
                hasUnverifiedInputs(competitors) ||
                isCheckingProfile
              }
            >
              {isCheckingProfile ? "Sprawdzam profil..." : "Przejdź do płatności"}
            </Button>

            {hasUnverifiedInputs(competitors) && (
              <p className="text-center text-sm text-destructive">
                Sprawdź wszystkie profile przed kontynuacją
              </p>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200/60">
            <Button
              variant="ghost"
              onClick={() => navigate("/onboarding/inspiration-sources")}
              className="w-full text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Wstecz
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Confirm dialog — final review of verified competitor profiles
          before transitioning to pricing. Skipped on re-entry when the
          lineup matches the previously approved signature (CONFIRMED_KEY). */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md bg-white/95 border border-white/70 shadow-2xl rounded-2xl">
          <AlertDialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-secondary to-secondary/80 rounded-2xl flex items-center justify-center shadow-md">
                <Target className="h-7 w-7 text-white" />
              </div>
            </div>
            <AlertDialogTitle className="text-center text-xl font-semibold tracking-tight text-gray-900">
              Potwierdź profile
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-center space-y-4">
                <div className="bg-white/60 rounded-xl p-4 my-4 border border-white/80 shadow-sm">
                  <ul className="space-y-2">
                    {Object.values(verifications)
                      .filter(v => v.status === "success" && v.username)
                      .map((v, idx) => (
                        <li
                          key={idx}
                          className="flex items-center justify-center gap-2 text-base font-medium text-gray-900"
                        >
                          {v.profile_pic_url ? (
                            <ProfileAvatar url={v.profile_pic_url} fullName={v.full_name} size={24} />
                          ) : (
                            <div className="w-5 h-5 bg-secondary/10 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-secondary" />
                            </div>
                          )}
                          @{v.username}
                        </li>
                      ))}
                  </ul>
                </div>
                <p className="text-sm text-gray-500">Czy nazwy profili są poprawne?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-2 sm:!justify-between sm:!space-x-0">
            <AlertDialogCancel className="w-full sm:w-auto rounded-xl bg-white/60 border border-gray-200/60 text-gray-700 hover:bg-primary/10 hover:border-primary/30 hover:text-primary shadow-sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Popraw
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmContinue}
              className="w-full sm:w-auto rounded-xl shadow-sm"
            >
              Tak, kontynuuj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default AddCompetitors;
